/**
 * AddContactModal - Modal para crear un contacto manualmente
 * Con validación robusta de teléfono chileno y acciones rápidas opcionales
 */

import { useState, useEffect, useRef } from 'react'
import { contactAPI, authAPI } from '../../services/api'
import AgentSelector from '../shared/AgentSelector'

// Colores aleatorios para listas nuevas
const RANDOM_COLORS = [
  '#EF4444', '#F97316', '#EAB308', '#22C55E', '#10B981',
  '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899', '#D946EF'
]

/**
 * Normaliza un teléfono chileno a formato canónico con +56
 * Acepta: 976400600, 56976400600, +56976400600
 * Retorna: +56976400600
 */
function normalizeChileanPhone(input) {
  const digits = input.replace(/[^\d]/g, '')

  // 9 dígitos empezando con 9 → celular chileno sin código país
  if (digits.length === 9 && digits.startsWith('9')) {
    return `+56${digits}`
  }
  // 11 dígitos empezando con 56 → celular chileno con código país
  if (digits.length === 11 && digits.startsWith('56')) {
    return `+${digits}`
  }
  // 12 dígitos empezando con 569 (raro pero posible si incluyeron el +)
  if (digits.length === 12 && digits.startsWith('569')) {
    return `+${digits.slice(0, 11)}`
  }
  // Otros formatos internacionales: retornar con + si tiene 10+ dígitos
  if (digits.length >= 10) {
    return `+${digits}`
  }
  // Muy corto, retornar tal cual
  return digits
}

/**
 * Genera las variantes de búsqueda para un teléfono
 * Para que el backend pueda encontrar duplicados sin importar el formato
 */
function getPhoneVariants(input) {
  const digits = input.replace(/[^\d]/g, '')
  const variants = new Set()

  // Variante original
  variants.add(digits)

  // Si empieza con 9 y tiene 9 dígitos → agregar con 56 y +56
  if (digits.length === 9 && digits.startsWith('9')) {
    variants.add(`56${digits}`)
    variants.add(`+56${digits}`)
  }
  // Si empieza con 56 y tiene 11 dígitos → agregar sin 56 y con +56
  if (digits.length === 11 && digits.startsWith('56')) {
    variants.add(digits.slice(2)) // sin código país
    variants.add(`+${digits}`)   // con +
  }
  // Si empieza con +56
  if (input.startsWith('+56')) {
    const withoutPlus = digits // ya sin +
    variants.add(withoutPlus)
    variants.add(withoutPlus.slice(2)) // sin código país
  }

  return [...variants]
}

export default function AddContactModal({ onClose, onCreated, onNavigateToContact, folders = [] }) {
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [selectedAgentId, setSelectedAgentId] = useState(null)
  const [selectedAgentName, setSelectedAgentName] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Validación de duplicado
  const [checking, setChecking] = useState(false)
  const [duplicate, setDuplicate] = useState(null)
  const checkTimeoutRef = useRef(null)

  // Acciones rápidas opcionales
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [dueDate, setDueDate] = useState('')
  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const [selectedFolderName, setSelectedFolderName] = useState(null)
  const [newListName, setNewListName] = useState('')
  const [showNewListInput, setShowNewListInput] = useState(false)

  // Pre-seleccionar agente del usuario logueado
  useEffect(() => {
    const currentUser = authAPI.getCurrentUser()
    if (currentUser?.name) {
      setSelectedAgentName(currentUser.name)
      // El ID real de Wassenger se resolverá cuando AgentSelector cargue el team
    }
  }, [])

  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current)
    }
  }, [])

  const handlePhoneChange = (e) => {
    const raw = e.target.value
    const cleaned = raw.replace(/[^\d+]/g, '')
    setPhone(cleaned)
    setError(null)
    setDuplicate(null)

    const digits = cleaned.replace(/[^\d]/g, '')
    if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current)

    if (digits.length >= 8) {
      setChecking(true)
      checkTimeoutRef.current = setTimeout(async () => {
        try {
          // El backend genera todas las variantes, basta enviar los dígitos
          const data = await contactAPI.checkDuplicate(digits)
          if (data && data.exists) {
            setDuplicate({
              name: data.name || null,
              phone: data.phone,
              assignedTo: data.assignedTo || null
            })
          } else {
            setDuplicate(null)
          }
        } catch {
          setDuplicate(null)
        } finally {
          setChecking(false)
        }
      }, 500)
    } else {
      setChecking(false)
    }
  }

  const digits = phone.replace(/[^\d]/g, '')
  const isPhoneValid = digits.length >= 8 && digits.length <= 15
  const normalizedPreview = isPhoneValid ? normalizeChileanPhone(phone) : null
  const canSubmit = isPhoneValid && !duplicate && !checking && !saving

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!canSubmit) return

    setSaving(true)
    setError(null)

    try {
      const result = await contactAPI.createContact({
        phone: normalizeChileanPhone(phone),
        name: name.trim() || undefined,
        agentId: selectedAgentId || undefined,
        agentName: selectedAgentName || undefined,
        dueDate: dueDate || undefined,
        folderId: selectedFolderId || undefined,
        newListName: (!selectedFolderId && newListName.trim()) ? newListName.trim() : undefined
      })

      if (onCreated) onCreated(result)
      onClose()
    } catch (err) {
      if (err.response?.status === 409) {
        const existing = err.response.data.existingContact
        setDuplicate({
          name: existing?.name || null,
          phone: existing?.phone || phone,
          assignedTo: existing?.assignedTo || null
        })
      } else {
        setError(err.response?.data?.error || 'Error creando contacto')
      }
    } finally {
      setSaving(false)
    }
  }

  // Click en contacto duplicado → navegar al contacto
  const handleDuplicateClick = () => {
    if (!duplicate) return
    if (onNavigateToContact) {
      onNavigateToContact(duplicate.phone)
    }
    onClose()
  }

  // Helpers para fechas rápidas
  const setQuickDate = (daysFromNow) => {
    const d = new Date()
    d.setDate(d.getDate() + daysFromNow)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    setDueDate(`${yyyy}-${mm}-${dd}`)
  }

  const customFolders = folders.filter(f => !f.es_sistema && !f.archivada)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-dark-alt rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white">Nuevo Contacto</h3>
          <p className="text-primary-100 text-sm">Crear contacto manualmente</p>
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Teléfono / WhatsApp <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="Ej: 912345678 o +56912345678"
                className={`w-full px-4 py-3 border rounded-xl text-sm dark:bg-surface-dark dark:text-gray-100 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition ${
                  duplicate ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-surface-dark-border'
                }`}
                autoFocus
              />
              {checking && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            {duplicate && (
              <button
                type="button"
                onClick={handleDuplicateClick}
                className="mt-1.5 w-full text-left p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition group"
              >
                <p className="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                  <span>⚠️</span>
                  <span>Este número ya existe:</span>
                </p>
                <div className="mt-1 flex items-center gap-2 text-sm">
                  <span className="font-semibold text-gray-800 dark:text-gray-100">
                    {duplicate.name || 'Sin nombre'}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">{duplicate.phone}</span>
                  {duplicate.assignedTo && (
                    <span className="ml-auto text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                      {duplicate.assignedTo}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 group-hover:text-amber-700 dark:group-hover:text-amber-300">
                  Click para ver este contacto →
                </p>
              </button>
            )}
            {!duplicate && normalizedPreview && normalizedPreview !== phone && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Se guardará como: <span className="font-medium text-gray-700 dark:text-gray-200">{normalizedPreview}</span>
              </p>
            )}
            {!duplicate && phone && !isPhoneValid && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Ingresa entre 8 y 15 dígitos
              </p>
            )}
          </div>

          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Nombre
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del contacto"
              className="w-full px-4 py-3 border border-gray-300 dark:border-surface-dark-border dark:bg-surface-dark dark:text-gray-100 dark:placeholder-gray-500 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
            />
          </div>

          {/* Agente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Asignar a agente
            </label>
            <AgentSelector
              phone={null}
              currentAgent={selectedAgentName}
              onAssign={(agentName, agentId) => {
                setSelectedAgentName(agentName)
                setSelectedAgentId(agentId)
              }}
              mode="select"
              dropdownDirection="up"
            />
          </div>

          {/* Toggle acciones rápidas */}
          <button
            type="button"
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-surface-dark border border-gray-200 dark:border-surface-dark-border rounded-xl text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover transition"
          >
            <span className="flex items-center gap-2">
              <span>⚡</span>
              <span>Acciones rápidas</span>
              {(dueDate || selectedFolderId || newListName.trim()) && (
                <span className="w-2 h-2 rounded-full bg-primary-500" />
              )}
            </span>
            <svg className={`w-4 h-4 transition-transform ${showQuickActions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Panel de acciones rápidas */}
          {showQuickActions && (
            <div className="space-y-4 pl-2 border-l-2 border-primary-200 dark:border-primary-800">
              {/* Fecha de vencimiento */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                  📅 Fecha de vencimiento
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-surface-dark-border dark:bg-surface-dark dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
                />
                <div className="flex gap-1.5 mt-1.5">
                  <button type="button" onClick={() => setQuickDate(1)} className="px-2 py-1 text-xs bg-gray-100 dark:bg-surface-dark-hover border border-gray-200 dark:border-surface-dark-border text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition">Mañana</button>
                  <button type="button" onClick={() => setQuickDate(7)} className="px-2 py-1 text-xs bg-gray-100 dark:bg-surface-dark-hover border border-gray-200 dark:border-surface-dark-border text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition">+7 días</button>
                  <button type="button" onClick={() => setQuickDate(30)} className="px-2 py-1 text-xs bg-gray-100 dark:bg-surface-dark-hover border border-gray-200 dark:border-surface-dark-border text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition">+1 mes</button>
                  {dueDate && (
                    <button type="button" onClick={() => setDueDate('')} className="px-2 py-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition">Quitar</button>
                  )}
                </div>
              </div>

              {/* Asignar a lista existente */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                  📋 Agregar a lista
                </label>
                {customFolders.length > 0 ? (
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {customFolders.map(folder => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => {
                          if (selectedFolderId === folder.id) {
                            setSelectedFolderId(null)
                            setSelectedFolderName(null)
                          } else {
                            setSelectedFolderId(folder.id)
                            setSelectedFolderName(folder.nombre)
                            setNewListName('')
                            setShowNewListInput(false)
                          }
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition ${
                          selectedFolderId === folder.id
                            ? 'bg-primary-50 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 font-medium'
                            : 'bg-gray-50 dark:bg-surface-dark border border-gray-200 dark:border-surface-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-hover'
                        }`}
                      >
                        <span>{folder.icono || '📋'}</span>
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: folder.color || '#6B7280' }}
                        />
                        <span className="flex-1 truncate">{folder.nombre}</span>
                        {selectedFolderId === folder.id && (
                          <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500">No hay listas personalizadas</p>
                )}

                {/* Crear lista nueva */}
                {!selectedFolderId && (
                  <>
                    {showNewListInput ? (
                      <div className="flex gap-2 mt-2">
                        <input
                          type="text"
                          value={newListName}
                          onChange={(e) => setNewListName(e.target.value)}
                          placeholder="Nombre de la nueva lista"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-surface-dark-border dark:bg-surface-dark dark:text-gray-100 dark:placeholder-gray-500 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => { setShowNewListInput(false); setNewListName('') }}
                          className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowNewListInput(true)}
                        className="mt-2 w-full flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-surface-dark-border rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-400 transition"
                      >
                        <span>+</span>
                        <span>Crear lista nueva</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error general */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-surface-dark-border flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-xl transition font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition disabled:opacity-50 font-medium shadow-md"
          >
            {saving ? 'Creando...' : 'Crear Contacto'}
          </button>
        </div>
      </div>
    </div>
  )
}
